import { ErrorBoundary } from '@sentry/react'
import React, { Suspense } from 'react'
import { Navigate } from 'react-router'

import { useAuth } from '../../../hooks/useAuth'
import { ROUTES } from '../../../lib/routes'
import errorTypeService from '../../../lib/services/errorTypeService'
import FullPageErrorFallback from '../../misc/FullPageErrorFallback'
import LoadingWidget from '../../widgets/LoadingWidget'
import CreateAccountModal from './components/ClaimReferralModal'
import Header from './components/Header'
import ReferralInviteAlreadyClaimedError from './components/ReferralInviteClaimedError'
import { useReferralInvitesQuery } from './hooks/useReferralInvitesQuery'

interface Props {
  children: React.ReactNode
}

const ReferralInviteLayoutContent: React.FC<Props> = ({ children }) => {
  const { isAuthenticated, currentCompany, isClientUser } = useAuth()
  const shouldCompleteCompanySetup =
    isAuthenticated && !isClientUser && !currentCompany

  useReferralInvitesQuery()

  if (shouldCompleteCompanySetup) {
    return <Navigate to={ROUTES.addCompany} replace />
  }

  return (
    <>
      <Header />
      {children}
      <CreateAccountModal />
    </>
  )
}

const ReferralInviteLayout: React.FC<Props> = ({ children }) => (
  <Suspense fallback={<LoadingWidget />}>
    <ErrorBoundary
      fallback={(error) => {
        const isPermissionError =
          errorTypeService.isForbiddenError(error.error) ||
          errorTypeService.isUnauthorizedError(error.error)

        return isPermissionError ? (
          <ReferralInviteAlreadyClaimedError />
        ) : (
          <FullPageErrorFallback error={error} />
        )
      }}
    >
      <ReferralInviteLayoutContent>{children}</ReferralInviteLayoutContent>
    </ErrorBoundary>
  </Suspense>
)

export default ReferralInviteLayout
